import { memo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  DRIVER_TYPE_NAMES,
  DR_DRIVER_TYPE,
  NON_ODF_DRIVER_TYPES,
  ODF_DRIVER_TYPE,
} from "../../../consts/driverTypes";
import {
  FETCH_DRIVER_CREATION_USERS_SUCCESS,
  FETCH_DRIVER_CREATION_USERS_ERROR,
} from "../../../consts/messages";
import { GET_NON_DRIVER_USERS } from "../../../consts/apis";
import { NONE } from "../../../consts/strings";
import {
  drDriverCodeRegex,
  odfDriverCodeRegex,
} from "../../../consts/driverManagement";

import api from "../../../services/api";

import {
  updateDriverProfile,
  validateDriverProfileEdit,
} from "../../../utils/drivers";

import {
  addDriverModalOpenSelector,
  getSelectedDepot,
} from "../../../reducers/ownApp/selectors";
import {
  addNotification,
  setAddDriverModalOpen,
  setLoading,
} from "../../../reducers/ownApp";
import { lookupTableSelector } from "../../../reducers/depot/selectors";
import {
  addDriver,
  setNewDriverEdit,
  setNewDriverProfileEdit,
  setNewDriverUsers,
} from "../../../reducers/driverManagement/thunkActions";
import {
  driverProfilesSelector,
  newDriverCodeSelector,
  newDriverEditSelector,
  newDriverProfileEditSelector,
  newDriverSelectedUserSelector,
  newDriverTypesSelector,
  newDriverUsersSelector,
  selectingDriverLocationSelector,
} from "../../../reducers/driverManagement/selectors";

import { Modal } from "../Modal";
import { AddDriverContent } from "./AddDriverContent";
import { AddDriverActions } from "./AddDriverActions";

export const AddDriverModal = memo(() => {
  const dispatch = useDispatch();
  const selectedDepot = useSelector(getSelectedDepot);
  const lookupTables = useSelector(lookupTableSelector);
  const open = useSelector(addDriverModalOpenSelector);
  const selectingDriverLocation = useSelector(selectingDriverLocationSelector);
  const driverProfiles = useSelector(driverProfilesSelector);
  const users = useSelector(newDriverUsersSelector);
  const selectedUser = useSelector(newDriverSelectedUserSelector);
  const driverTypes = useSelector(newDriverTypesSelector);
  const driverCode = useSelector(newDriverCodeSelector);
  const driverEdit = useSelector(newDriverEditSelector);
  const driverProfileEdit = useSelector(newDriverProfileEditSelector);

  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(true);

  const getFullDriverCode = (userDriverCode) =>
    `${selectedDepot}*${userDriverCode}`;
  const getDisplayableDriverCode = (fullDriverCode) =>
    fullDriverCode.split("*")[1] || "";

  const setInitialState = (keepUsers) => {
    setErrors({});
    setIsValid(true);
    dispatch(
      setNewDriverEdit({
        users: keepUsers ? users : [{ userId: NONE, userName: NONE }],
        selectedUser: { userId: NONE, userName: NONE },
        driverTypes: [{ label: NONE, value: NONE }],
        driverProfileEdit: {
          driverCode: "",
          driverName: "",
          driverType: NONE,
          phoneNumber: "",
          vehicleType: "",
          securityCheckDate: null,
          saturnLockdown: false,
          notAtHomeSameDay: true,
          namePronunciation: "",
          startDate: null,
          averageDeliveriesPerWeek: 0,
          countryFlagCode: null,
          favouriteSong: null,
          biography: null,
          photoCode: null,
          specialConsiderations: null,
        },
      })
    );
  };

  useEffect(() => {
    if (open && !users) {
      dispatch(setLoading(true));
      setInitialState();
      (async () => {
        try {
          const { data } = await api.get(GET_NON_DRIVER_USERS(selectedDepot));
          dispatch(
            setNewDriverUsers([{ userId: NONE, userName: NONE }, ...data.users])
          );
          dispatch(
            addNotification({
              message: FETCH_DRIVER_CREATION_USERS_SUCCESS,
              options: { variant: "success" },
            })
          );
        } catch (error) {
          setInitialState();
          dispatch(
            addNotification({
              message: FETCH_DRIVER_CREATION_USERS_ERROR,
              options: { variant: "error" },
            })
          );
          throw error;
        } finally {
          dispatch(setLoading(false));
        }
      })();
    } else if (!open) dispatch(setNewDriverEdit(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!driverProfileEdit?.driverCode) setErrors(false);
    else {
      const { errors } = validateDriverProfileEdit({
        driverEdit: { driverProfileEdit },
        excludeDriversMap: driverProfiles,
        lookupTables,
      });
      setErrors(errors);
    }
  }, [driverProfileEdit, driverProfiles, lookupTables]);

  useEffect(() => {
    setIsValid(
      !(
        !selectedUser ||
        selectedUser.userId === NONE ||
        !!Object.keys(errors).length
      )
    );
  }, [selectedUser, errors]);

  const onUserChange = ({ target }) => {
    const selectedUser = users.find(({ userId }) => userId === target.value);
    if (!selectedUser || selectedUser.userId === NONE) {
      setInitialState(true);
      return;
    }

    const userDriverCode = target.value.replace(/[^0-9A-Z]/gi, ""); // TODO consider replacing with populating with selectedUser.saturnLogon
    const fullDriverCode = getFullDriverCode(userDriverCode);
    let driverTypes = null;
    let newDriverProfileEdit = {
      ...driverProfileEdit,
      driverName: selectedUser.userName,
      driverCode: fullDriverCode,
    };
    if (odfDriverCodeRegex.test(fullDriverCode)) {
      driverTypes = [
        { value: ODF_DRIVER_TYPE, label: DRIVER_TYPE_NAMES[ODF_DRIVER_TYPE] },
      ];
      newDriverProfileEdit.driverType = ODF_DRIVER_TYPE;
    } else if (drDriverCodeRegex.test(fullDriverCode)) {
      driverTypes = [
        { value: DR_DRIVER_TYPE, label: DRIVER_TYPE_NAMES[DR_DRIVER_TYPE] },
      ];
      newDriverProfileEdit.driverType = DR_DRIVER_TYPE;
    } else {
      driverTypes = [
        { label: NONE, value: NONE },
        ...Object.entries(NON_ODF_DRIVER_TYPES).map(([value, label]) => ({
          value,
          label,
        })),
      ];
      newDriverProfileEdit.driverType = NONE;
    }
    dispatch(
      setNewDriverEdit({
        ...driverEdit,
        selectedUser,
        driverTypes,
        driverProfileEdit: newDriverProfileEdit,
      })
    );
  };

  const onDriverCodeChange = ({ target }) => {
    const userDriverCode = target.value.toUpperCase();

    if (
      !open ||
      !selectedDepot ||
      !selectedUser ||
      selectedUser.userId === NONE
    ) {
      setInitialState();
      return;
    }

    dispatch(
      setNewDriverProfileEdit({
        ...driverProfileEdit,
        driverCode: getFullDriverCode(userDriverCode),
      })
    );
  };

  const onDriverProfileEditChange = ({ updates = [] }) => {
    if (!selectedUser || selectedUser.userId === NONE) return;

    const newDriverProfileEdit = updateDriverProfile({
      updates,
      driverProfileEdit,
    });

    dispatch(setNewDriverProfileEdit(newDriverProfileEdit));
  };

  const onClose = () => dispatch(setAddDriverModalOpen(false));
  const onContinue = async () => {
    dispatch(
      addDriver({
        user: selectedUser,
        driverProfile: driverProfileEdit,
      })
    );
    onClose();
  };

  return !driverEdit || selectingDriverLocation ? null : (
    <Modal
      open={open}
      onClose={onClose}
      title={"ADD NEW DRIVER"}
      content={
        <AddDriverContent
          users={users}
          selectedUser={selectedUser}
          onUserChange={onUserChange}
          driverCode={getDisplayableDriverCode(driverCode)}
          onDriverCodeChange={onDriverCodeChange}
          driverTypes={driverTypes}
          driverProfileEdit={driverProfileEdit}
          onDriverProfileEditChange={onDriverProfileEditChange}
          errors={errors}
        />
      }
      actions={
        <AddDriverActions
          disabled={!isValid}
          onCancel={onClose}
          onContinue={onContinue}
        />
      }
      offsetLeft={180}
    />
  );
});
